import { useCallback, useEffect, useRef, useState } from 'react';
import { CHAT_DICTATION_MAX_DURATION_SECONDS } from '@shared/schemas/chat-dictation';
import {
  DICTATION_MESSAGES,
  getDictationErrorMessage,
  getMicrophoneErrorMessage,
} from '../chat-dictation-errors';
import { transcribeDictation } from '../services/chat-dictation.api';

export type ChatDictationStatus = 'idle' | 'starting' | 'recording' | 'transcribing';

/** Opus first (Chrome, Firefox, Edge); Safari only records AAC in MP4. */
const RECORDER_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/ogg;codecs=opus',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/webm',
];
/** Speech-grade Opus; the whole 2-minute clip stays under 0.5 MB. */
const RECORDER_BITS_PER_SECOND = 32_000;
const MAX_RECORDING_MS = CHAT_DICTATION_MAX_DURATION_SECONDS * 1_000;

type DictationSession = {
  abort?: AbortController;
  audioContext?: AudioContext;
  chunks: Blob[];
  finishing: boolean;
  recorder?: MediaRecorder;
  stream?: MediaStream;
  timer?: ReturnType<typeof setTimeout>;
};

type UseChatDictationOptions = {
  onError: (message: string) => void;
  onTranscript: (text: string) => void;
  /** Changing it (e.g. another conversation) cancels any recording or pending transcription. */
  scopeKey?: string;
};

function pickRecorderMimeType(): string | undefined {
  const isTypeSupported = window.MediaRecorder?.isTypeSupported;
  if (typeof isTypeSupported !== 'function') {
    return undefined;
  }
  return RECORDER_MIME_CANDIDATES.find((candidate) => MediaRecorder.isTypeSupported(candidate));
}

function getSupportError(): string | null {
  if (typeof window.MediaRecorder !== 'function') {
    return DICTATION_MESSAGES.unsupported;
  }
  if (typeof navigator.mediaDevices?.getUserMedia !== 'function') {
    return window.isSecureContext === false ? DICTATION_MESSAGES.insecure : DICTATION_MESSAGES.unsupported;
  }
  return null;
}

function releaseMedia(session: DictationSession) {
  clearTimeout(session.timer);
  session.stream?.getTracks().forEach((track) => track.stop());
  session.stream = undefined;
  void session.audioContext?.close().catch(() => undefined);
  session.audioContext = undefined;
}

function createAnalyser(session: DictationSession, stream: MediaStream): AnalyserNode | null {
  try {
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 1_024;
    audioContext.createMediaStreamSource(stream).connect(analyser);
    session.audioContext = audioContext;
    return analyser;
  } catch {
    // The level meter is cosmetic: recording works without it.
    return null;
  }
}

export function useChatDictation({ onError, onTranscript, scopeKey }: UseChatDictationOptions) {
  const [status, setStatus] = useState<ChatDictationStatus>('idle');
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const sessionRef = useRef<DictationSession | null>(null);
  const callbacksRef = useRef({ onError, onTranscript });

  useEffect(() => {
    callbacksRef.current = { onError, onTranscript };
  }, [onError, onTranscript]);

  const settle = useCallback((session: DictationSession) => {
    if (sessionRef.current !== session) {
      return false;
    }
    sessionRef.current = null;
    setAnalyser(null);
    setStatus('idle');
    return true;
  }, []);

  const fail = useCallback((session: DictationSession, message: string) => {
    releaseMedia(session);
    if (settle(session)) {
      callbacksRef.current.onError(message);
    }
  }, [settle]);

  const cancel = useCallback(() => {
    const session = sessionRef.current;
    if (!session) {
      return;
    }
    session.abort?.abort();
    const recorder = session.recorder;
    if (recorder && recorder.state !== 'inactive') {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.stop();
    }
    releaseMedia(session);
    settle(session);
  }, [settle]);

  const finish = useCallback(async () => {
    const session = sessionRef.current;
    const recorder = session?.recorder;
    if (!session || !recorder || session.finishing || recorder.state === 'inactive') {
      return;
    }
    session.finishing = true;
    clearTimeout(session.timer);
    const stopped = new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
    });
    recorder.stop();
    setAnalyser(null);
    setStatus('transcribing');
    await stopped;
    // The microphone is released before the network round-trip.
    releaseMedia(session);
    if (sessionRef.current !== session) {
      return;
    }

    const audio = new Blob(session.chunks, { type: recorder.mimeType || session.chunks[0]?.type || 'audio/webm' });
    if (audio.size === 0) {
      fail(session, DICTATION_MESSAGES.empty);
      return;
    }

    session.abort = new AbortController();
    try {
      const { text } = await transcribeDictation(audio, session.abort.signal);
      if (!settle(session)) {
        return;
      }
      const transcript = text.trim();
      if (transcript) {
        callbacksRef.current.onTranscript(transcript);
      } else {
        callbacksRef.current.onError(DICTATION_MESSAGES.noSpeech);
      }
    } catch (error) {
      fail(session, getDictationErrorMessage(error));
    }
  }, [fail, settle]);

  const start = useCallback(async () => {
    if (sessionRef.current) {
      return;
    }
    const supportError = getSupportError();
    if (supportError) {
      callbacksRef.current.onError(supportError);
      return;
    }

    const session: DictationSession = { chunks: [], finishing: false };
    sessionRef.current = session;
    setStatus('starting');

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { autoGainControl: true, channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
    } catch (error) {
      fail(session, getMicrophoneErrorMessage(error));
      return;
    }
    session.stream = stream;
    if (sessionRef.current !== session) {
      // Canceled (unmount, conversation change) while the permission prompt was open.
      releaseMedia(session);
      return;
    }

    let recorder: MediaRecorder;
    try {
      const mimeType = pickRecorderMimeType();
      recorder = new MediaRecorder(stream, {
        audioBitsPerSecond: RECORDER_BITS_PER_SECOND,
        ...(mimeType ? { mimeType } : {}),
      });
    } catch {
      fail(session, DICTATION_MESSAGES.unsupported);
      return;
    }

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        session.chunks.push(event.data);
      }
    };
    stream.getAudioTracks().forEach((track) => {
      track.onended = () => {
        if (!session.finishing) {
          cancel();
          callbacksRef.current.onError(DICTATION_MESSAGES.micDisconnected);
        }
      };
    });
    session.recorder = recorder;
    recorder.start();
    // Hitting the limit transcribes what was said rather than dropping it.
    session.timer = setTimeout(() => void finish(), MAX_RECORDING_MS);
    setAnalyser(createAnalyser(session, stream));
    setStatus('recording');
  }, [cancel, fail, finish]);

  // Always release the microphone: unmount and conversation change.
  useEffect(() => cancel, [cancel, scopeKey]);

  return { analyser, cancel, finish, start, status };
}
